import { useCallback, useState, type ReactNode } from 'react';
import { notifications } from '@mantine/notifications';
import { useForm } from 'react-hook-form';
import { Breadcrumbs, OptionsTable, type OptionsTableColumn } from '@components';
import {
  Button,
  Checkbox,
  ControlledInput,
  DoubleCheckModal,
  Modal,
  QuietAddTrigger,
  RefetchButton,
} from '@shared/ui';
import { Loader } from '@mantine/core';
import { formatPhoneNumber } from '@shared/utils/format-phone';
import { usePickupPoints } from '@modules/pickup-points';
import {
  formatPickupPhoneForInput,
  toApiPhone,
} from '@modules/pickup-points/utils/formatPickupPhone';
import {
  SELLER_STAFF_SECTION_LABELS,
  SELLER_STAFF_SECTIONS,
  useCreateSellerStaff,
  useDeleteSellerStaff,
  useSellerStaff,
  useUpdateSellerStaff,
  type SellerStaffAccess,
  type SellerStaffSection,
} from '@modules/seller-staff';
import '@shared/styles/page.css';
import './SellerAccessesPage.css';

interface StaffFormData {
  phone: string;
  email: string;
  name: string;
  position: string;
  ORDERS: boolean;
  PRODUCTS: boolean;
  REVIEWS: boolean;
  CONTACTS: boolean;
  PROMOTION: boolean;
  PROMO_CODES: boolean;
  BONUSES: boolean;
  FINANCE: boolean;
  orderPickupPointIds: number[];
}

const defaultFormValues: StaffFormData = {
  phone: '',
  email: '',
  name: '',
  position: '',
  ORDERS: false,
  PRODUCTS: false,
  REVIEWS: false,
  CONTACTS: false,
  PROMOTION: false,
  PROMO_CODES: false,
  BONUSES: false,
  FINANCE: false,
  orderPickupPointIds: [],
};

function isValidEmailFormat(value: string): boolean {
  const s = value.trim();
  if (!s) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

function sectionsFromForm(data: StaffFormData): SellerStaffSection[] {
  return SELLER_STAFF_SECTIONS.filter((key) => data[key]);
}

function needsPickupPoints(data: Pick<StaffFormData, 'ORDERS' | 'PRODUCTS' | 'CONTACTS'>): boolean {
  return data.ORDERS || data.PRODUCTS || data.CONTACTS;
}

function staffToForm(row: SellerStaffAccess): StaffFormData {
  const set = new Set(row.sections);
  return {
    phone: row.phone ? formatPickupPhoneForInput(row.phone) : '',
    email: row.email ?? '',
    name: row.name ?? '',
    position: row.position ?? '',
    ORDERS: set.has('ORDERS'),
    PRODUCTS: set.has('PRODUCTS'),
    REVIEWS: set.has('REVIEWS'),
    CONTACTS: set.has('CONTACTS'),
    PROMOTION: set.has('PROMOTION'),
    PROMO_CODES: set.has('PROMO_CODES'),
    BONUSES: set.has('BONUSES'),
    FINANCE: set.has('FINANCE'),
    orderPickupPointIds: row.orderPickupPointIds ?? [],
  };
}

function staffLoginLabel(row: SellerStaffAccess): string {
  const parts: string[] = [];
  if (row.phone) {
    parts.push(formatPhoneNumber(row.phone) || row.phone);
  }
  if (row.email?.trim()) {
    parts.push(row.email.trim());
  }
  return parts.join(' / ') || 'сотрудник';
}

function BulletList({ items }: { items: string[] }): ReactNode {
  if (items.length === 0) {
    return <span className="seller-accesses-muted">—</span>;
  }
  return (
    <ul className="seller-accesses-bullets">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

export default function SellerAccessesPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [toDelete, setToDelete] = useState<SellerStaffAccess | null>(null);
  const { data: pickupPoints = [] } = usePickupPoints();
  const { data: staff = [], isLoading, error, refetch, isFetching } = useSellerStaff();
  const createStaff = useCreateSellerStaff();
  const updateStaff = useUpdateSellerStaff();
  const deleteStaff = useDeleteSellerStaff();

  const { handleSubmit, reset, control, watch, setValue } =
    useForm<StaffFormData>({
      defaultValues: defaultFormValues,
    });

  const ordersEnabled = watch('ORDERS');
  const productsEnabled = watch('PRODUCTS');
  const contactsEnabled = watch('CONTACTS');
  const pointsEnabled = ordersEnabled || productsEnabled || contactsEnabled;
  const selectedPointIds = watch('orderPickupPointIds');

  const isPending = createStaff.isPending || updateStaff.isPending;
  const isEdit = editingId != null;

  const openCreate = () => {
    setEditingId(null);
    reset(defaultFormValues);
    setModalOpen(true);
  };

  const openEdit = useCallback(
    (row: SellerStaffAccess) => {
      setEditingId(row.id);
      reset(staffToForm(row));
      setModalOpen(true);
    },
    [reset],
  );

  const closeModal = () => {
    if (isPending) return;
    setModalOpen(false);
    setEditingId(null);
    reset(defaultFormValues);
  };

  const onSubmit = async (data: StaffFormData) => {
    const sections = sectionsFromForm(data);
    if (sections.length === 0) {
      notifications.show({
        title: 'Выберите разделы',
        message: 'Сотруднику нужен хотя бы один раздел',
        color: 'red',
      });
      return;
    }
    if (needsPickupPoints(data) && data.orderPickupPointIds.length === 0) {
      notifications.show({
        title: 'Выберите точки',
        message: 'Укажите хотя бы одну точку самовывоза',
        color: 'red',
      });
      return;
    }
    if (!isEdit) {
      const phoneRaw = data.phone.trim();
      const emailRaw = data.email.trim().toLowerCase();
      const phone = phoneRaw ? toApiPhone(phoneRaw) : undefined;
      const email = emailRaw || undefined;

      if (!phone && !email) {
        notifications.show({
          title: 'Укажите контакты для входа',
          message: 'Нужен телефон и/или email — хотя бы одно из двух',
          color: 'red',
        });
        return;
      }
      if (phone && !/^\+7\d{10}$/.test(phone)) {
        notifications.show({
          title: 'Некорректный номер',
          message: 'Введите номер в формате +7…',
          color: 'red',
        });
        return;
      }
      if (email && !isValidEmailFormat(email)) {
        notifications.show({
          title: 'Некорректный email',
          message: 'Проверьте адрес электронной почты',
          color: 'red',
        });
        return;
      }
      await createStaff.mutateAsync({
        ...(phone ? { phone } : {}),
        ...(email ? { email } : {}),
        name: data.name.trim() || undefined,
        position: data.position.trim() || undefined,
        sections,
        orderPickupPointIds: needsPickupPoints(data) ? data.orderPickupPointIds : [],
      });
    } else if (editingId != null) {
      await updateStaff.mutateAsync({
        id: editingId,
        data: {
          name: data.name.trim() || null,
          position: data.position.trim() || null,
          sections,
          orderPickupPointIds: needsPickupPoints(data) ? data.orderPickupPointIds : [],
        },
      });
    }
    setModalOpen(false);
    setEditingId(null);
    reset(defaultFormValues);
  };

  const columns: OptionsTableColumn<SellerStaffAccess>[] = [
    {
      key: 'phone',
      label: 'Телефон',
      render: (row) =>
        row.phone ? formatPhoneNumber(row.phone) || row.phone : (
          <span className="seller-accesses-muted">—</span>
        ),
    },
    {
      key: 'email',
      label: 'Email',
      render: (row) =>
        row.email?.trim() || <span className="seller-accesses-muted">—</span>,
    },
    {
      key: 'name',
      label: 'Имя',
      render: (row) => row.name?.trim() || '—',
    },
    {
      key: 'position',
      label: 'Должность',
      render: (row) => row.position?.trim() || '—',
    },
    {
      key: 'sections',
      label: 'Разделы',
      render: (row) => (
        <BulletList
          items={row.sections.map((s) => SELLER_STAFF_SECTION_LABELS[s] ?? s)}
        />
      ),
    },
    {
      key: 'orderPickupPointIds',
      label: 'Точки',
      render: (row) => {
        if (
          !row.sections.includes('ORDERS') &&
          !row.sections.includes('CONTACTS') &&
          !row.sections.includes('PRODUCTS')
        ) {
          return <span className="seller-accesses-muted">—</span>;
        }
        const labels = (row.orderPickupPointIds ?? []).map((id) => {
          const p = pickupPoints.find((x) => x.id === id);
          return p ? `${p.city} — ${p.address}` : `#${id}`;
        });
        return <BulletList items={labels} />;
      },
    },
  ];

  const getActions = useCallback(
    (item: SellerStaffAccess) => [
      { label: 'Изменить', onClick: () => openEdit(item) },
      { label: 'Удалить', onClick: () => setToDelete(item) },
    ],
    [openEdit],
  );

  return (
    <div className="page-container seller-accesses-page">
      <Breadcrumbs items={[{ label: 'Главная', path: '/' }, { label: 'Доступы' }]} />

      <div className="page-header">
        <div className="page-header-top">
          <div className="seller-accesses-title-row">
            <h1>Доступы</h1>
            <QuietAddTrigger onClick={openCreate} aria-label="Добавить сотрудника">
              Добавить сотрудника
            </QuietAddTrigger>
          </div>
        </div>
        <p className="seller-accesses-subtitle">
          Сотрудник входит по телефону (SMS) и/или email — укажите хотя бы один способ.
          Если указаны оба, войти можно любым. Отметьте нужные разделы; «Мои данные» и
          этот список доступов остаются только у владельца.
        </p>
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={isEdit ? 'Изменить доступ' : 'Добавить сотрудника'}
        size={520}
        classNames={{ body: 'seller-accesses-modal-body' }}
      >
        <form onSubmit={handleSubmit(onSubmit)} className="seller-accesses-form">
          <div className="seller-accesses-form__body">
            <div className="seller-accesses-form-row">
              <ControlledInput
                id="phone"
                type="tel"
                inputMode="tel"
                label={isEdit ? 'Телефон' : 'Телефон (или email)'}
                placeholder="+7 (900) 000-00-00"
                control={control}
                name="phone"
                formatOnChange={formatPickupPhoneForInput}
                disabled={isEdit || isPending}
              />
              <ControlledInput
                id="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                label={isEdit ? 'Email' : 'Email (или телефон)'}
                placeholder="name@example.com"
                control={control}
                name="email"
                disabled={isEdit || isPending}
              />
            </div>
            <div className="seller-accesses-form-row">
              <ControlledInput
                id="name"
                type="text"
                label="Имя (необязательно)"
                control={control}
                name="name"
                disabled={isPending}
              />
              <ControlledInput
                id="position"
                type="text"
                label="Должность (необязательно)"
                placeholder="Например, флорист"
                control={control}
                name="position"
                disabled={isPending}
              />
            </div>
            <div className="seller-accesses-sections">
              <span className="seller-accesses-sections-label">Разделы</span>
              {SELLER_STAFF_SECTIONS.map((key) => (
                <Checkbox
                  key={key}
                  label={SELLER_STAFF_SECTION_LABELS[key]}
                  checked={watch(key)}
                  disabled={isPending}
                  onChange={(e) => setValue(key, e.currentTarget.checked)}
                />
              ))}
              {pointsEnabled ? (
                <div className="seller-accesses-points">
                  <span className="seller-accesses-sections-label">Точки самовывоза</span>
                  {pickupPoints.length === 0 ? (
                    <p className="seller-accesses-points-empty">
                      Сначала добавьте точки самовывоза в разделе «Точки самовывоза».
                    </p>
                  ) : (
                    pickupPoints.map((point) => (
                      <Checkbox
                        key={point.id}
                        label={`${point.city} — ${point.address}`}
                        checked={(selectedPointIds ?? []).includes(point.id)}
                        disabled={isPending}
                        onChange={(e) => {
                          const checked = e.currentTarget.checked;
                          const current = selectedPointIds ?? [];
                          setValue(
                            'orderPickupPointIds',
                            checked
                              ? [...current, point.id]
                              : current.filter((id) => id !== point.id),
                          );
                        }}
                      />
                    ))
                  )}
                </div>
              ) : null}
            </div>
          </div>
          <div className="seller-accesses-form-actions">
            <Button type="button" variant="secondary" onClick={closeModal} disabled={isPending}>
              Отмена
            </Button>
            <Button type="submit" variant="primary" disabled={isPending} loading={isPending}>
              {isEdit ? 'Сохранить' : 'Добавить'}
            </Button>
          </div>
        </form>
      </Modal>

      <DoubleCheckModal
        isOpen={toDelete != null}
        onClose={() => setToDelete(null)}
        title="Отозвать доступ?"
        message={
          toDelete
            ? `Сотрудник ${staffLoginLabel(toDelete)} больше не сможет войти в кабинет магазина.`
            : undefined
        }
        firstButtonText="Отмена"
        secondButtonText="Удалить"
        secondButtonAction={() => {
          if (!toDelete) return;
          deleteStaff.mutateAsync(toDelete.id).then(() => setToDelete(null));
        }}
        isLoading={deleteStaff.isPending}
      />

      <div className="content-wrapper seller-accesses-content">
        <div className="seller-accesses-desktop-table">
          <OptionsTable<SellerStaffAccess>
            columns={columns}
            data={staff}
            loading={isLoading || isFetching}
            error={error}
            refetch={refetch}
            getActions={getActions}
            emptyMessage="Сотрудников пока нет — добавьте телефон и/или email"
          />
        </div>

        <div className="seller-accesses-mobile-list" aria-label="Список доступов">
          {error && refetch ? (
            <RefetchButton
              refetch={refetch}
              isLoading={isLoading || isFetching}
              errorMessage={error instanceof Error ? error.message : 'Не удалось загрузить доступы'}
            />
          ) : isLoading ? (
            <div className="seller-accesses-mobile-empty">
              <Loader size="sm" />
            </div>
          ) : staff.length === 0 ? (
            <div className="seller-accesses-mobile-empty" role="status">
              Сотрудников пока нет — добавьте телефон и/или email
            </div>
          ) : (
            staff.map((row) => {
              const sectionLabels = row.sections.map(
                (s) => SELLER_STAFF_SECTION_LABELS[s] ?? s,
              );
              const showPoints =
                row.sections.includes('ORDERS') ||
                row.sections.includes('CONTACTS') ||
                row.sections.includes('PRODUCTS');
              const pointLabels = showPoints
                ? (row.orderPickupPointIds ?? []).map((id) => {
                    const p = pickupPoints.find((x) => x.id === id);
                    return p ? `${p.city} — ${p.address}` : `#${id}`;
                  })
                : [];
              const phoneLabel = row.phone
                ? formatPhoneNumber(row.phone) || row.phone
                : null;
              const emailLabel = row.email?.trim() || null;

              return (
                <div key={row.id} className="seller-accesses-mobile-card">
                  <div className="seller-accesses-mobile-card__top">
                    <div className="seller-accesses-mobile-card__identity">
                      <span className="seller-accesses-mobile-card__name">
                        {row.name?.trim() || 'Без имени'}
                      </span>
                      {row.position?.trim() ? (
                        <span className="seller-accesses-mobile-card__position">
                          {row.position.trim()}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="seller-accesses-mobile-card__contacts">
                    {phoneLabel ? <span>{phoneLabel}</span> : null}
                    {emailLabel ? <span>{emailLabel}</span> : null}
                    {!phoneLabel && !emailLabel ? (
                      <span className="seller-accesses-muted">—</span>
                    ) : null}
                  </div>
                  <div className="seller-accesses-mobile-card__block">
                    <span className="seller-accesses-mobile-card__label">Разделы</span>
                    <BulletList items={sectionLabels} />
                  </div>
                  {showPoints ? (
                    <div className="seller-accesses-mobile-card__block">
                      <span className="seller-accesses-mobile-card__label">Точки</span>
                      <BulletList items={pointLabels} />
                    </div>
                  ) : null}
                  <div className="seller-accesses-mobile-card__actions">
                    <button
                      type="button"
                      className="seller-accesses-mobile-card__btn"
                      onClick={() => openEdit(row)}
                    >
                      Изменить
                    </button>
                    <button
                      type="button"
                      className="seller-accesses-mobile-card__btn seller-accesses-mobile-card__btn--danger"
                      onClick={() => setToDelete(row)}
                    >
                      Удалить
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
